import { gql, useLazyQuery, useMutation, useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import ModalFotos from "layouts/authentication/components/ModalFotos";
import { validateDNI } from "utils/validations";
import { validateCUIL } from "utils/validations";
import { validateEmail } from "utils/validations";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};

export default function ModalAddSolicitud({ open, handleClose, refetchParent }) {
  const [proveedor, setProveedor] = useState({
    tipoDocumento: "DNI",
  });
  const { handleSnackbar } = useContext(MessageManager);
  const [modalFotosOpen, setModalFotosOpen] = useState(false);
  const [modalFotosOpenAdj, setModalFotosOpenAdj] = useState(false);

  const [addProveedorUser, { loading: loadingSave }] = useMutation(gql`
    mutation addUserProveedor($input: UserConvenioInput!) {
      saveUserConvenio(input: $input) {
        id
      }
    }
  `);

  const { data: provincias } = useQuery(gql`
    query GetProvincias {
      provincias {
        id
        nombre
      }
    }
  `);

  const { data: dataConvenios } = useQuery(gql`
    query GetConvenios {
      convenios {
        id
        nombreInstitucion
      }
    }
  `);

  const [getLocalidades, { data: localidades }] = useLazyQuery(gql`
    query GetLocalidades($provincia: ID!) {
      localidades(provincia: $provincia) {
        id
        nombre
        provincia {
          id
        }
      }
    }
  `);

  const handleSave = () => {
    const { provincia, localidad, ...rest } = proveedor;
    addProveedorUser({
      variables: {
        input: { ...rest, idLocalidad: rest.idLocalidad },
      },
    })
      .then(() => {
        handleSnackbar("Usuario creado correctamente, ya puedes iniciar sesión", "success");
        refetchParent();
        handleClose();
      })
      .catch((error) => {
        handleSnackbar(error.message || "Error al agregar el usuario", "error");
      });
  };

  const handleChanges = (e) => {
    setProveedor({
      ...proveedor,
      [e.target.name]: e.target.value,
    });
  };

  const handleOpenModalFotos = () => {
    setModalFotosOpen(true);
  };

  const handleOpenModalFotosAdj = () => {
    setModalFotosOpenAdj(true);
  };

  useEffect(() => {
    if (proveedor?.provincia) {
      getLocalidades({ variables: { provincia: proveedor?.provincia } }).then((data) => {
        if (!data?.data?.localidades?.find((localidad) => localidad.id === proveedor?.localidad)) {
          setProveedor({
            ...proveedor,
            localidad: -1,
          });
        }
      });
    }
  }, [proveedor?.provincia]);

  useEffect(() => {
    if(!open){
      setProveedor({
        tipoDocumento: "DNI",
      });
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox display="flex" justifyContent="space-between">
            <SoftBox>
              <SoftTypography variant="h6">Agregar Solicitud</SoftTypography>
            </SoftBox>
            <SoftBox display="flex" justifyContent="space-between">
              <SoftBox>
                <Tooltip title="Cerrar" placement="top">
                  <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                    close
                  </Icon>
                </Tooltip>
              </SoftBox>
            </SoftBox>
          </SoftBox>

          <SoftBox>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} lg={4}>
                <InputLabel htmlFor="nombre">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Nombre
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="nombre"
                  placeholder="Ingrese nombre..."
                  type="text"
                  name="nombre"
                  value={proveedor?.nombre || ""}
                  onChange={handleChanges}
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={4}>
                <InputLabel htmlFor="apellido">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Apellido
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="apellido"
                  placeholder="Ingrese apellido..."
                  type="text"
                  name="apellido"
                  value={proveedor?.apellido || ""}
                  onChange={handleChanges}
                />
              </Grid>

              <Grid item xs={12} sm={4} lg={4}>
                <InputLabel htmlFor="usuario">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Correo electrónico
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*&nbsp;
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  type="text"
                  placeholder="Correo electrónico"
                  name="email"
                  value={proveedor.email}
                  onChange={handleChanges}
                  autoComplete="new-password"
                  error={
                    proveedor.email && proveedor.email.length > 0 && !validateEmail(proveedor.email)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={4} lg={3}>
                <InputLabel htmlFor="provincia">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Provincia
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color="primary"
                    ></SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="provincia"
                  value={proveedor?.provincia || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  {provincias?.provincias?.map((provincia) => (
                    <MenuItem key={provincia.id} value={provincia.id}>
                      {provincia.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={4} lg={3}>
                <InputLabel htmlFor="localidad">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Localidad
                    <SoftTypography
                      variant="caption"
                      fontWeight="bold"
                      color="primary"
                    ></SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="idLocalidad"
                  value={proveedor?.idLocalidad || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>
                  {localidades?.localidades?.map((localidad) => (
                    <MenuItem key={localidad.id} value={localidad.id}>
                      {localidad.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={4} lg={3}>
                <InputLabel htmlFor="idConvenio">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Convenio
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="idConvenio"
                  value={proveedor?.idConvenio || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione un convenio
                  </MenuItem>

                  {dataConvenios?.convenios?.map((convenio) => (
                    <MenuItem key={convenio.id} value={convenio.id}>
                      {convenio.nombreInstitucion}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={4} lg={3}>
                <InputLabel htmlFor="dni">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    DNI
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  label="documento"
                  placeholder="DNI (sin puntos ni guiones)"
                  type="text"
                  name="dni"
                  value={proveedor?.dni || ""}
                  error={
                    proveedor?.tipoDocumento == "DNI" && proveedor?.dni?.length > 0
                      ? !validateDNI(proveedor?.dni)
                      : proveedor?.dni?.length > 0 && !validateCUIL(proveedor?.dni)
                  }
                  onChange={handleChanges}
                />
              </Grid>

              <Grid item xs={12} sm={4} lg={4}>
                <InputLabel htmlFor="tipoUsuario">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo Usuario
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  name="tipoUsuario"
                  value={proveedor?.tipoUsuario || -1}
                  onChange={handleChanges}
                >
                  <MenuItem value={-1} disabled>
                    Seleccione uno
                  </MenuItem>

                  <MenuItem value={"DOCENTE"}>DOCENTE</MenuItem>
                  <MenuItem value={"ALUMNO"}>ALUMNO</MenuItem>
                  <MenuItem value={"PERSONALAPOYO"}>PERSONAL DE APOYO</MenuItem>
                  <MenuItem value={"EXTERNO"}>EXTERNO</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} md={6} lg={4} alignSelf={"center"}>
                <InputLabel htmlFor="tipoUsuario">
                  <SoftTypography variant="caption" fontWeight="bold">
                    Documentos
                  </SoftTypography>
                </InputLabel>
                <SoftButton
                  onClick={handleOpenModalFotos}
                  color="primary"
                  circular
                  sx={{ width: { xs: "100%" } }}
                >
                  <Icon sx={{ mr: 1 }}>upload_file</Icon> DNI
                </SoftButton>
              </Grid>

              <Grid item xs={12} md={6} lg={4} alignSelf={"center"}>
                <InputLabel htmlFor="tipoUsuario">
                  <SoftTypography variant="caption" fontWeight="bold">
                    &nbsp;
                  </SoftTypography>
                </InputLabel>
                <SoftButton
                  onClick={handleOpenModalFotosAdj}
                  color="primary"
                  circular
                  disabled={!proveedor?.tipoUsuario}
                  sx={{ width: { xs: "100%" } }}
                >
                  <Icon sx={{ mr: 1 }}>upload_file</Icon>{" "}
                  {proveedor?.tipoUsuario == "ALUMNO"
                    ? "Certificadod de alumno regular"
                    : "Certificado de prestación de servicios"}
                </SoftButton>
              </Grid>
            </Grid>
            <SoftBox mt={4} display="flex" justifyContent="flex-end" alignItems="center">
              <SoftBox mr={2}>
                <SoftButton type="submit" color="primary" fullWidth circular onClick={handleClose}>
                  <Icon sx={{ fontWeight: "light" }}>close</Icon>
                  &nbsp; Cancelar
                </SoftButton>
              </SoftBox>
              <SoftBox>
                <SoftButton
                  type="submit"
                  color="uproGreen"
                  fullWidth
                  circular
                  disabled={
                    loadingSave ||
                    !proveedor.nombre ||
                    !proveedor.apellido ||
                    !proveedor.email ||
                    !proveedor.idConvenio ||
                    !proveedor.tipoDocumento ||
                    !proveedor.dni ||
                    !proveedor.tipoUsuario ||
                    !proveedor?.documentosFile?.length > 0 ||
                    !proveedor?.certificadoFile?.length > 0
                  }
                  onClick={handleSave}
                >
                  {loadingSave ? (
                    <CircularProgress size={17} color="white" sx={{ mx: 6 }} />
                  ) : (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      Crear cuenta &nbsp;
                      <Icon sx={{ fontWeight: "light" }}>arrow_forward</Icon>
                    </div>
                  )}
                </SoftButton>
              </SoftBox>
            </SoftBox>
            <ModalFotos
              open={modalFotosOpen}
              handleClose={() => setModalFotosOpen(false)}
              proveedor={proveedor}
              handleSubmit={(files) => {
                setProveedor({ ...proveedor, documentosFile: files });
              }}
              esDNI={true}
            />
            <ModalFotos
              open={modalFotosOpenAdj}
              handleClose={() => setModalFotosOpenAdj(false)}
              proveedor={proveedor}
              handleSubmit={(files) => {
                setProveedor({ ...proveedor, certificadoFile: files });
              }}
              esDNI={false}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddSolicitud.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetchParent: PropTypes.func,
};
